import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import type {
  AgentEditorsLightResponseBody,
  AgentEditorsResponseBody,
  PatchAgentEditorsRequestBody,
} from "@app/types/api/assistant/configuration/editors";
import { pluralize } from "@app/types/shared/utils/string_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher } from "swr";

export function useEditors({
  owner,
  agentConfigurationId,
  disabled,
}: {
  owner: LightWorkspaceType;
  agentConfigurationId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const editorsFetcher: Fetcher<
    AgentEditorsResponseBody | AgentEditorsLightResponseBody
  > = fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    agentConfigurationId
      ? `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfigurationId}/editors`
      : null,

    editorsFetcher,
    {
      disabled,
    }
  );

  return {
    editors: data?.editors ?? emptyArray(),
    isEditorsLoading: !error && !data && !disabled,
    isEditorsError: !!error,
    isEditorsValidating: isValidating,
    mutateEditors: mutate,
  };
}

export function useUpdateEditors({
  owner,
  agentConfigurationId,
}: {
  owner: LightWorkspaceType;
  agentConfigurationId: string | null;
}) {
  const sendNotification = useSendNotification();
  const { mutateEditors } = useEditors({
    owner,
    agentConfigurationId,
    disabled: true,
  });

  const updateAgentEditors = useCallback(
    async (body: PatchAgentEditorsRequestBody) => {
      if (!agentConfigurationId) {
        return false;
      }

      const res = await clientFetch(
        `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfigurationId}/editors`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (res.ok) {
        await mutateEditors();

        let title = "";
        let description: string | undefined = undefined;
        if (
          body.addEditorIds != null &&
          body.addEditorIds.length > 0 &&
          body.removeEditorIds != null &&
          body.removeEditorIds.length > 0
        ) {
          title = "Successfully update editors";
          description = "Successfully added and removed editors";
        } else if (
          (body.addEditorIds == null || body.addEditorIds.length <= 0) &&
          body.removeEditorIds != null &&
          body.removeEditorIds.length > 0
        ) {
          title = `Successfully removed editor${pluralize(body.removeEditorIds.length)}`;
        } else {
          title = `Successfully added editor${pluralize(body.addEditorIds?.length ?? 0)}`;
        }

        sendNotification({
          type: "success",
          title,
          description,
        });
        return true;
      }

      sendNotification({
        type: "error",
        title: "Failed to update editors",
      });
      return false;
    },
    [owner, agentConfigurationId, mutateEditors, sendNotification]
  );

  return updateAgentEditors;
}
